import { useRef } from "react";
import type { CSSProperties } from "react";
import { ArrowDown, ArrowUpRight, AtSign, Code2, Layers3, MoveRight, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { ContactForm } from "../components/ContactForm";
import { ReactiveGrid } from "../components/ReactiveGrid";
import { projects } from "../data/projects";
import { useGsap } from "../hooks/useGsap";
import "../styles/home.css";

const projectMeta = [
  ["Исследование", "Редакционный UX", "GSAP"],
  ["Арт-дирекшн", "E-commerce", "Story scroll"],
  ["Интерактивная карта", "Калькулятор", "Data UI"],
  ["Сервисный UX", "Доступность", "Запись"],
  ["Иммерсивный арт", "Live UI", "Билеты"],
  ["Каталог и фильтры", "Личный кабинет", "Открытая CMS"],
] as const;

const projectAccents = ["#b9ecff", "#d9a188", "#9cff72", "#ffb25f", "#c7ff2f", "#85a4ff"] as const;

const capabilities = [
  "Стратегия",
  "Продающий сценарий",
  "Арт-дирекшн",
  "React",
  "GSAP",
  "Адаптив",
  "Формы",
  "SEO",
  "Запуск",
] as const;

const manifesto =
  "Каждый лендинг получает собственную логику, ритм и визуальный язык. Не перекрашиваю один шаблон — проектирую путь от первого экрана до целевого действия.";

export function HomePage() {
  const page = useRef<HTMLElement>(null);

  useGsap(page, (gsap) => {
    const intro = gsap.timeline({ defaults: { ease: "expo.out" } });
    intro
      .from(".portfolio-nav", { y: -28, autoAlpha: 0, duration: 0.8 })
      .from(".portfolio-hero__eyebrow", { y: 18, autoAlpha: 0, duration: 0.65 }, 0.12)
      .from(".portfolio-hero__title-line > span", {
        yPercent: 112,
        rotate: 2,
        duration: 1.08,
        stagger: 0.08,
      }, 0.16)
      .from(".portfolio-hero__lead, .portfolio-hero__actions", {
        y: 24,
        autoAlpha: 0,
        duration: 0.78,
        stagger: 0.1,
      }, 0.46)
      .from(".portfolio-hero__frame", {
        y: 90,
        rotation: 5,
        scale: 0.86,
        autoAlpha: 0,
        duration: 1.15,
        stagger: 0.12,
      }, 0.24)
      .from(".portfolio-hero__foot > *", {
        y: 16,
        autoAlpha: 0,
        duration: 0.6,
        stagger: 0.06,
      }, 0.68);

    gsap.to(".portfolio-hero__stage", {
      yPercent: 14,
      ease: "none",
      scrollTrigger: {
        trigger: ".portfolio-hero",
        start: "top top",
        end: "bottom top",
        scrub: 1,
      },
    });

    gsap.to(".portfolio-manifesto__word", {
      opacity: 1,
      stagger: 0.04,
      ease: "none",
      scrollTrigger: {
        trigger: ".portfolio-manifesto",
        start: "top 74%",
        end: "bottom 46%",
        scrub: 1,
      },
    });

    gsap.utils.toArray<HTMLElement>(".portfolio-card").forEach((card) => {
      const image = card.querySelector("img");
      const content = card.querySelectorAll(".portfolio-card__topline, .portfolio-card__content");

      gsap.from(content, {
        y: 30,
        autoAlpha: 0,
        duration: 0.82,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger: card,
          start: "top 82%",
          once: true,
        },
      });

      if (image) {
        gsap.fromTo(image, {
          scale: 1.14,
          yPercent: -4,
        }, {
          scale: 1.01,
          yPercent: 4,
          ease: "none",
          scrollTrigger: {
            trigger: card,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.1,
          },
        });
      }
    });

    const methodCards = gsap.utils.toArray<HTMLElement>(".portfolio-method__item");
    methodCards.forEach((card, index) => {
      const contents = card.querySelectorAll("svg, h2, p");
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: card,
          start: "top 86%",
          end: "top 48%",
          scrub: 0.8,
        },
      });

      timeline
        .fromTo(card, {
          y: 110,
          scale: 0.9,
          rotationX: 13,
          transformPerspective: 1200,
          transformOrigin: "50% 100%",
        }, {
          y: 0,
          scale: 1,
          rotationX: 0,
          ease: "power3.out",
        })
        .from(contents, {
          y: 34,
          autoAlpha: 0,
          stagger: 0.08,
          ease: "power3.out",
        }, 0.08);

      const trace = card.querySelector(".portfolio-method__trace");
      if (trace) {
        gsap.fromTo(trace, {
          xPercent: index % 2 === 0 ? -18 : 18,
          yPercent: 12,
        }, {
          xPercent: index % 2 === 0 ? 18 : -18,
          yPercent: -12,
          ease: "none",
          scrollTrigger: {
            trigger: card,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
          },
        });
      }
    });

    gsap.from(".portfolio-contact__inner > *", {
      y: 42,
      autoAlpha: 0,
      stagger: 0.09,
      duration: 0.9,
      ease: "power3.out",
      scrollTrigger: {
        trigger: ".portfolio-contact",
        start: "top 70%",
      },
    });
  });

  return (
    <main id="main" ref={page} className="portfolio-index">
      <section className="portfolio-hero" aria-labelledby="portfolio-title">
        <ReactiveGrid
          className="portfolio-hero__grid"
          color="90, 126, 255"
          cell={54}
          radius={260}
          strength={30}
        />
        <div className="portfolio-hero__noise" aria-hidden="true" />

        <header className="portfolio-nav">
          <a className="portfolio-nav__brand" href="#top" aria-label="На начало страницы">
            <span>Vivor</span>
            <b>.tech</b>
          </a>
          <nav aria-label="Основная навигация">
            <a href="#approach">Подход</a>
            <a href="#work">Работы</a>
          </nav>
          <a
            className="portfolio-nav__contact"
            href="https://t.me/MrVivor"
            target="_blank"
            rel="noopener noreferrer"
            data-magnetic
          >
            Написать в Telegram <Send aria-hidden="true" />
          </a>
        </header>

        <div className="portfolio-hero__copy" id="top">
          <p className="portfolio-hero__eyebrow">Разработка лендингов и сайтов под ключ</p>
          <h1 id="portfolio-title">
            <span className="portfolio-hero__title-line"><span>Лендинги,</span></span>
            <span className="portfolio-hero__title-line"><span>которые ведут</span></span>
            <span className="portfolio-hero__title-line"><span>к заявке</span></span>
          </h1>
          <p className="portfolio-hero__lead">
            Проектирую смысл, пишу продающий сценарий, создаю визуальную систему и собираю
            быстрый сайт на React с точной GSAP-анимацией. Один человек отвечает за результат
            от первого экрана до запуска.
          </p>
          <div className="portfolio-hero__actions">
            <a className="portfolio-button portfolio-button--light" href="#contact" data-magnetic>
              Рассказать о задаче <MoveRight aria-hidden="true" />
            </a>
            <a className="portfolio-button portfolio-button--ghost" href="#work">
              Смотреть проекты <ArrowDown aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="portfolio-hero__stage" aria-hidden="true">
          {[projects[2], projects[0], projects[1]].map((project, index) => (
            <div className={`portfolio-hero__frame portfolio-hero__frame--${index + 1}`} key={project.slug} data-tilt>
              <div className="portfolio-hero__framebar">
                <i /><i /><i />
                <span>{project.slug}.studio</span>
              </div>
              <img src={project.image} alt="" width="800" height="900" />
              <b>{project.title}</b>
            </div>
          ))}
        </div>

        <div className="portfolio-hero__foot">
          <span>Шесть разных индустрий</span>
          <span>Шесть работающих сайтов</span>
          <a href="#approach">Как устроена работа <ArrowDown aria-hidden="true" /></a>
        </div>
      </section>

      <div className="portfolio-marquee" aria-hidden="true">
        <div className="portfolio-marquee__track">
          {[0, 1].map((group) => (
            <div className="portfolio-marquee__group" key={group}>
              {[0, 1].flatMap((cycle) => capabilities.map((item) => (
                <span key={`${group}-${cycle}-${item}`}>{item}<i>↗</i></span>
              )))}
            </div>
          ))}
        </div>
      </div>

      <section className="portfolio-manifesto" id="approach">
        <div className="portfolio-manifesto__aside">
          <p>Сначала — решение бизнес-задачи. Потом — эффектный визуал.</p>
          <div>
            <span>Сценарий страницы</span>
            <span>Тексты и структура</span>
            <span>Дизайн-система</span>
            <span>Разработка и запуск</span>
          </div>
        </div>
        <p className="portfolio-manifesto__statement" aria-label={manifesto}>
          {manifesto.split(" ").map((word, index) => (
            <span className="portfolio-manifesto__word" aria-hidden="true" key={`${word}-${index}`}>
              {word}{" "}
            </span>
          ))}
        </p>
      </section>

      <section className="portfolio-method" aria-label="Что входит в работу">
        <article className="portfolio-method__item" data-spotlight>
          <div className="portfolio-method__trace" aria-hidden="true"><i /><i /><i /></div>
          <Layers3 aria-hidden="true" />
          <h2>Собираю путь к решению</h2>
          <p>
            Выстраиваю экран за экраном: от понятного обещания и демонстрации продукта
            до снятия возражений и сильного призыва к действию.
          </p>
        </article>
        <article className="portfolio-method__item" data-spotlight>
          <div className="portfolio-method__trace" aria-hidden="true"><i /><i /><i /></div>
          <Code2 aria-hidden="true" />
          <h2>Дизайн сразу живёт в коде</h2>
          <p>
            Адаптив, формы, интерактивность и анимации проверяются в браузере, а не остаются
            красивой статичной картинкой в макете.
          </p>
        </article>
        <article className="portfolio-method__item portfolio-method__item--accent" data-spotlight>
          <div className="portfolio-method__trace" aria-hidden="true"><i /><i /><i /></div>
          <ArrowUpRight aria-hidden="true" />
          <h2>Каждый проект выглядит своим</h2>
          <p>
            Визуальный язык рождается из продукта и аудитории. Поэтому экспедиция, парфюм,
            агротехнологии, детский центр, фестиваль и магазин не похожи друг на друга.
          </p>
        </article>
      </section>

      <section className="portfolio-work" id="work" aria-labelledby="portfolio-work-title">
        <div className="portfolio-work__heading">
          <p>Работы можно открыть и пройти как реальные сайты</p>
          <h2 id="portfolio-work-title" data-split>Шесть миров. Один стандарт исполнения</h2>
          <p>
            Разные аудитории, типографика, логика продажи и язык движения. Общими остаются
            аккуратная разработка, адаптивность и внимание к деталям.
          </p>
        </div>

        <div className="portfolio-grid" aria-label="Список проектов">
          {projects.map((project, index) => (
            <Link
              className={`portfolio-card portfolio-card--${index + 1}`}
              key={project.slug}
              to={`/${project.slug}`}
              style={{
                "--project-color": project.color,
                "--project-accent": projectAccents[index],
              } as CSSProperties}
              aria-label={`Открыть проект ${project.title}`}
            >
              <div className="portfolio-card__media">
                <img
                  src={index === 4 ? "/assets/nullfield/crowd.webp" : project.image}
                  alt=""
                  width="1200"
                  height="900"
                  loading={index < 2 ? "eager" : "lazy"}
                />
              </div>
              <div className="portfolio-card__wash" aria-hidden="true" />
              <div className="portfolio-card__topline">
                <span>{project.type}</span>
                <span>Открыть сайт <ArrowUpRight aria-hidden="true" /></span>
              </div>
              <div className="portfolio-card__content">
                <div>
                  <h3>{project.title}</h3>
                  <p>{project.note}</p>
                </div>
                <ul aria-label="Ключевые особенности">
                  {projectMeta[index].map((item) => <li key={item}>{item}</li>)}
                </ul>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="portfolio-contact" id="contact">
        <ReactiveGrid
          className="portfolio-contact__grid"
          color="255, 255, 255"
          cell={64}
          radius={230}
          strength={24}
        />
        <div className="portfolio-contact__inner">
          <div className="portfolio-contact__copy">
            <p className="portfolio-contact__eyebrow">Новый проект</p>
            <h2 data-split>Давайте превратим вашу идею в сайт, который продаёт</h2>
            <p className="portfolio-contact__lead">
              Можно оставить короткую заявку или написать напрямую. Я сам читаю все сообщения,
              уточняю задачу и предлагаю следующий шаг без менеджеров и длинных созвонов.
            </p>
            <div className="portfolio-contact__channels" aria-label="Способы связи">
              <a href="https://t.me/MrVivor" target="_blank" rel="noopener noreferrer" data-magnetic>
                <span><Send aria-hidden="true" /> Telegram</span>
                <strong>@MrVivor</strong>
                <ArrowUpRight aria-hidden="true" />
              </a>
              <a href="mailto:info@vivor.tech?subject=Новый проект для Vivor.tech" data-magnetic>
                <span><AtSign aria-hidden="true" /> Email</span>
                <strong>info@vivor.tech</strong>
                <ArrowUpRight aria-hidden="true" />
              </a>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="portfolio-footer">
        <p>Vivor.tech — design, copy, React & GSAP</p>
        <div className="portfolio-footer__links">
          <a href="https://t.me/MrVivor" target="_blank" rel="noopener noreferrer">@MrVivor</a>
          <a href="mailto:info@vivor.tech">info@vivor.tech</a>
        </div>
        <p>© 2026 Vivor.tech</p>
      </footer>
    </main>
  );
}
